import React, { useEffect, useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { Menu, X, Phone, Mail, Clock, MessageCircle } from 'lucide-react';
import { company, navLinks, waLink } from '../mock';
import { Button } from './ui/button';

export default function Header() {
  const [open, setOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const location = useLocation();
  const navigate = useNavigate();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 20);
    window.addEventListener('scroll', onScroll);
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  // Navigate to a section. Works from any route (goes to "/" first if needed).
  const goToSection = (e, href) => {
    e.preventDefault();
    setOpen(false);
    const id = (href || '#home').replace('#', '');
    const scrollTo = () => {
      const el = document.getElementById(id);
      if (el) el.scrollIntoView({ behavior: 'smooth' });
      else window.scrollTo({ top: 0, behavior: 'smooth' });
    };
    if (location.pathname !== '/') {
      navigate('/');
      setTimeout(scrollTo, 150);
    } else {
      scrollTo();
    }
  };

  return (
    <header className="fixed top-0 left-0 right-0 z-50">
      {/* Top utility bar */}
      <div className="hidden md:block bg-[#0B2447] text-slate-200 text-sm">
        <div className="max-w-7xl mx-auto px-6 flex items-center justify-between h-10">
          <div className="flex items-center gap-2">
            <Clock className="w-4 h-4 text-accent" />
            <span>{company.hours}</span>
          </div>
          <div className="flex items-center gap-6">
            <a href={`tel:${company.phoneRaw}`} className="flex items-center gap-2 hover:text-accent transition-colors">
              <Phone className="w-4 h-4" /> {company.phone}
            </a>
            <a href={`mailto:${company.email}`} className="flex items-center gap-2 hover:text-accent transition-colors">
              <Mail className="w-4 h-4" /> {company.email}
            </a>
          </div>
        </div>
      </div>

      {/* Main nav */}
      <div className={`transition-all duration-300 ${scrolled ? 'bg-white/95 backdrop-blur shadow-md' : 'bg-white'}`}>
        <div className="max-w-7xl mx-auto px-6 flex items-center justify-between h-[70px]">
          <Link to="/" onClick={(e) => goToSection(e, '#home')} className="flex items-center gap-3">
            <img src={company.logo} alt="Lockene logo" className="h-11 w-11 object-contain" />
            <div className="leading-none">
              <span className="text-2xl font-extrabold font-display brand-gradient">Lockene</span>
              <span className="block text-[11px] tracking-wide text-muted-foreground mt-0.5">Certification & Compliance</span>
            </div>
          </Link>

          <nav className="hidden lg:flex items-center gap-8">
            {navLinks.map((l) => (
              <a key={l.href} href={l.href} onClick={(e) => goToSection(e, l.href)} className="text-[15px] font-medium text-slate-700 hover:text-primary transition-colors relative after:absolute after:left-0 after:-bottom-1 after:h-0.5 after:w-0 after:bg-primary hover:after:w-full after:transition-all cursor-pointer">
                {l.label}
              </a>
            ))}
          </nav>

          <div className="hidden lg:flex items-center gap-3">
            <Button asChild className="inline-flex items-center justify-center bg-primary hover:bg-primary/90 text-white font-semibold rounded-full px-5 h-11 leading-none">
              <a href={`tel:${company.phoneRaw}`}><Phone className="w-4 h-4 mr-2" /> Call Now</a>
            </Button>
            <Button asChild variant="outline" className="inline-flex items-center justify-center border-2 border-[#25D366] text-[#128C4B] hover:bg-[#25D366] hover:text-white font-semibold rounded-full px-5 h-11 leading-none">
              <a href={waLink()} target="_blank" rel="noreferrer"><MessageCircle className="w-4 h-4 mr-2" /> WhatsApp</a>
            </Button>
          </div>

          <button className="lg:hidden p-2 text-slate-800" onClick={() => setOpen(!open)} aria-label="Menu">
            {open ? <X className="w-7 h-7" /> : <Menu className="w-7 h-7" />}
          </button>
        </div>

        {/* Mobile menu */}
        {open && (
          <div className="lg:hidden bg-white border-t border-slate-100 px-6 py-4">
            <nav className="flex flex-col gap-1">
              {navLinks.map((l) => (
                <a key={l.href} href={l.href} onClick={(e) => goToSection(e, l.href)} className="py-2.5 text-slate-700 font-medium border-b border-slate-50 cursor-pointer">
                  {l.label}
                </a>
              ))}
            </nav>
            <div className="flex gap-3 mt-4">
              <Button asChild className="flex-1 inline-flex items-center justify-center bg-primary text-white rounded-full h-11 leading-none">
                <a href={`tel:${company.phoneRaw}`}><Phone className="w-4 h-4 mr-2" /> Call</a>
              </Button>
              <Button asChild className="flex-1 inline-flex items-center justify-center bg-[#25D366] hover:bg-[#1fb457] text-white rounded-full h-11 leading-none">
                <a href={waLink()} target="_blank" rel="noreferrer"><MessageCircle className="w-4 h-4 mr-2" /> WhatsApp</a>
              </Button>
            </div>
          </div>
        )}
      </div>
    </header>
  );
}
